import React, {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useState,
  useMemo,
  useRef
} from 'react';
import dayjs from 'dayjs';
import SearchBox from 'components/common/SearchBox';
import {
  RecordingsTable,
  getRecordingsTableColumns
} from 'components/tables/RecordingsTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import * as noUiSlider from 'nouislider';
import 'nouislider/dist/nouislider.css';
import { fetchHVASearchCalls } from 'services/hvasearchcall.service';
import HVASpinner from 'components/base/HVASpinner';

interface Call {
  servicename: string;
  servicebrand: string;
  campaignname: string;
  callid: string;
  campaignid: number;
  audiosource: string;
  agent: string;
  accountref: string;
  customerref: string;
  customerfullname: string;
  phonenumber: string;
  calldatetime: string;
  duration: string;
}

type CampaignRecordingExplorerCallsProps = {
  service?: string;
  campaign?: string;
  hva?: string;
  firstName?: string;
  lastName?: string;
  accountID?: string;
  customerID?: string;
  phoneNumber?: string;
  startDate?: string;
  endDate?: string;
};

const CampaignRecordingExplorerCalls: React.FC<
  CampaignRecordingExplorerCallsProps
> = ({
  service,
  campaign,
  hva,
  firstName,
  lastName,
  accountID,
  customerID,
  phoneNumber,
  startDate,
  endDate
}) => {
  const [data, setData] = useState<Call[]>([]);
  const [maxDuration, setMaxDuration] = useState(300);
  // eslint-disable-next-line
  const [durationRange, setDurationRange] = useState<[number, number]>([0, 300]);
  const [isLoading, setIsLoading] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);
  const sliderInstanceRef = useRef<noUiSlider.API | null>(null);
  const [minDurationInput, setMinDurationInput] = useState<string>(
    durationRange[0].toString()
  );
  const [maxDurationInput, setMaxDurationInput] = useState<string>(
    durationRange[1].toString()
  );

  // Obtener las columnas de la tabla (sin callback porque no hay actualización automática)
  const columns = useMemo(() => getRecordingsTableColumns(), []);

  useEffect(() => {
    if (sliderRef.current && !sliderInstanceRef.current) {
      sliderInstanceRef.current = noUiSlider.create(sliderRef.current, {
        start: durationRange,
        connect: true,
        range: {
          min: 0,
          max: maxDuration || 300
        },
        step: 1
      });

      sliderInstanceRef.current.on('update', (values: (string | number)[]) => {
        const newMin = parseInt(values[0] as string, 10);
        const newMax = parseInt(values[1] as string, 10);

        setDurationRange([newMin, newMax]);
        setMinDurationInput(newMin.toString());
        setMaxDurationInput(newMax.toString());
      });
    }

    return () => {
      if (sliderInstanceRef.current) {
        sliderInstanceRef.current.destroy();
        sliderInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (sliderInstanceRef.current) {
      sliderInstanceRef.current.updateOptions(
        {
          range: {
            min: 0,
            max: maxDuration
          }
        },
        true
      );
      sliderInstanceRef.current.set([0, maxDuration]);
      setMinDurationInput('0');
      setMaxDurationInput(maxDuration.toString());
    }
  }, [maxDuration]);

  useEffect(() => {
    const fetchData = async () => {
      if (!startDate || !endDate) {
        setData([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const calls = await fetchHVASearchCalls({
          service,
          campaign,
          hva,
          firstName,
          lastName,
          accountID,
          customerID,
          phoneNumber,
          startDate,
          endDate
        });

        const transformedCalls = calls.map((call: Call) => ({
          ...call,
          calldatetime: dayjs(call.calldatetime).format('DD-MM-YYYY HH:mm:ss'),
          duration: call.duration.toString()
        }));

        setData(transformedCalls);

        if (calls.length === 0) {
          // Si no hay registros, establecer el máximo en 300
          setMaxDuration(300);
        } else {
          // Calcular la duración máxima
          const maxDurationValue = calls.reduce((max: number, call: Call) => {
            const duration = parseInt(call.duration, 10);
            return duration > max ? duration : max;
          }, 0);
          setMaxDuration(maxDurationValue);

          // Actualizar el rango de duración con el nuevo valor máximo
          setDurationRange([0, maxDurationValue]);
        }
      } catch (error) {
        alert('Error fetching calls: ' + error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    startDate,
    endDate,
    service,
    campaign,
    hva,
    firstName,
    lastName,
    accountID,
    customerID,
    phoneNumber
  ]);

  const filteredData = useMemo(() => {
    return data.filter(call => {
      const duration = parseInt(call.duration, 10);
      return duration >= durationRange[0] && duration <= durationRange[1];
    });
  }, [data, durationRange]);

  const table = useAdvanceTable({
    data: filteredData,
    columns: columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleMinDurationChange = (e: ChangeEvent<HTMLInputElement>) => {
    setMinDurationInput(e.target.value);
  };

  const handleMaxDurationChange = (e: ChangeEvent<HTMLInputElement>) => {
    setMaxDurationInput(e.target.value);
  };

  const handleMinDurationBlur = () => {
    let newMin = parseInt(minDurationInput, 10);

    // Validaciones
    if (isNaN(newMin)) newMin = 0;
    if (newMin < 0) newMin = 0;
    if (newMin > durationRange[1]) newMin = durationRange[1];

    // Actualizar el slider y el input
    if (sliderInstanceRef.current) {
      sliderInstanceRef.current.set([newMin, durationRange[1]]);
    }
  };

  const handleMaxDurationBlur = () => {
    let newMax = parseInt(maxDurationInput, 10);

    // Validaciones
    if (isNaN(newMax)) newMax = maxDuration;
    if (newMax > maxDuration) newMax = maxDuration;
    if (newMax < durationRange[0]) newMax = durationRange[0];

    // Actualizar el slider y el input
    if (sliderInstanceRef.current) {
      sliderInstanceRef.current.set([durationRange[0], newMax]);
    }
  };

  const handleDurationKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur(); // Pierde el foco para activar el evento onBlur
    }
  };

  return (
    <div>
      <div className="mb-4">
        <div className="d-flex flex-wrap gap-3">
          <SearchBox
            placeholder="Search calls"
            onChange={handleSearchInputChange}
            onKeyDown={handleSearchKeyPress}
          />
          <div className="d-flex align-items-center">
            <span className="fs-9 px-2 pe-1">Call Duration (sec.):</span>
            <input
              type="text"
              className="d-flex fs-9 fw-bold justify-content-end text-end"
              style={{
                width: '30px',
                border: 'none',
                background: 'transparent',
                outline: 'none'
              }}
              value={minDurationInput}
              onChange={handleMinDurationChange}
              onBlur={handleMinDurationBlur}
              onKeyDown={handleDurationKeyDown}
              aria-label="Minimum duration in seconds"
              title="Minimum duration in seconds"
            />
            <div
              className="noUi-primary-lighter noUi-handle-primary noUi-slider-slim noUi-handle-circle"
              ref={sliderRef}
              style={{
                width: '120px',
                margin: '12px 12px',
                visibility: 'visible'
              }}
            ></div>
            <input
              type="text"
              className="fs-9 fw-bold text-start"
              style={{
                width: '30px',
                border: 'none',
                background: 'transparent',
                outline: 'none'
              }}
              value={maxDurationInput}
              onChange={handleMaxDurationChange}
              onBlur={handleMaxDurationBlur}
              onKeyDown={handleDurationKeyDown}
              aria-label="Maximum duration in seconds"
              title="Maximum duration in seconds"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center my-5">
          <HVASpinner />
        </div>
      ) : (
        <div className="mb-9">
          <AdvanceTableProvider {...table}>
            <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 border-translucent position-relative top-1">
              <RecordingsTable />
            </div>
          </AdvanceTableProvider>
        </div>
      )}
    </div>
  );
};

export default CampaignRecordingExplorerCalls;
